/**
 * ============================================================================
 * MASTER PAGE: Countries & Messaging Rates (`tbl_country_master` & Mappings)
 * ============================================================================
 * Purpose:
 *   Comprehensive administration interface for WhatsApp Business Platform pricing:
 *   1. Official Meta Rate Card (effective October 1, 2026) with India Baseline &
 *      47 international markets (Marketing, Utility, Authentication, Authentication-Intl).
 *   2. Account-wise Country Permissions & Pricing:
 *      - India (+91) active by default with matching Display & Sell (Deduct) prices.
 *      - "Allow All International Destinations" toggle.
 *      - Dual pricing for every category: Display Price & Deduct Price (same by default).
 *   3. Telecom Carrier wholesale / delivery rate mappings.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { countryService, operatorService, accountService, userService } from "../../services";
import { getCurrentUser, isRegularUser } from "../../../core/utils/auth";

// SVG Icons
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const GlobeAmericasIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const XCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const InformationCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
  </svg>
);

const AdjustmentsHorizontalIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
  </svg>
);

const Cog6ToothIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const BanknotesIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6H2.25m0 0v8.25m0-8.25C6.444 6.75 12 6.75 17.25 6.75m0 0a3.75 3.75 0 0 1 3.75 3.75v.75m0-4.5c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125H3.75A1.125 1.125 0 0 1 2.625 18V5.625C2.625 5.004 3.129 4.5 3.75 4.5h16.5Z" />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ShieldCheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
  </svg>
);

export default function CountryManagement() {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isClient = useMemo(() => isRegularUser(currentUser), [currentUser]);

  // Regular user destination view state
  const [userDestinations, setUserDestinations] = useState([]);
  const [userAllowAllIntl, setUserAllowAllIntl] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("all");

  const [activeTab, setActiveTab] = useState("destinations"); // 'destinations' | 'operators' | 'accounts'

  // Top Section: Meta Official Baseline & Rates Table Toggle
  const [showMetaRatesTable, setShowMetaRatesTable] = useState(false);
  const [metaSearch, setMetaSearch] = useState("");
  const [metaCountries, setMetaCountries] = useState([]);
  const [loadingMetaRates, setLoadingMetaRates] = useState(false);

  // Tab 1: Country Master State
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countryPagination, setCountryPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [countrySearch, setCountrySearch] = useState("");

  // Tab 2: Country Operator Rates State
  const [operatorRates, setOperatorRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratePagination, setRatePagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  // Tab 3: Accounts Custom Rates & Permissions
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [operators, setOperators] = useState([]);

  // Account Rates & Permissions Modal Dialog State (Images 2 & 3)
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [isSavingPerms, setIsSavingPerms] = useState(false);
  const [allowAllIntl, setAllowAllIntl] = useState(false);
  const [accountEnabledRates, setAccountEnabledRates] = useState([]);
  const [accountOtherCountries, setAccountOtherCountries] = useState([]);
  const [permSearch, setPermSearch] = useState("");
  const [showAddCustomCountry, setShowAddCustomCountry] = useState(false);

  // Account User Policy Settings Modal Dialog State
  const [isUserSettingsModalOpen, setIsUserSettingsModalOpen] = useState(false);
  const [selectedUserSettingsAcc, setSelectedUserSettingsAcc] = useState(null);
  const [loadingUserSettings, setLoadingUserSettings] = useState(false);
  const [savingUserSettings, setSavingUserSettings] = useState(false);
  const [userSettingsSearch, setUserSettingsSearch] = useState("");
  const [userSettingsForm, setUserSettingsForm] = useState({
    bool_deduct_on_failed: true,
    bool_show_credits: true,
    bool_failed_cap_enabled: false,
    num_failed_cap_percent: 0,
    bool_refund_failed_credit: false,
    num_refund_delay_hours: 24,
  });

  // Custom Country Form State inside Perm Modal
  const [customCountryForm, setCustomCountryForm] = useState({
    str_country_name: "",
    str_calling_code: "",
    flt_marketing_display: 1.0,
    flt_marketing_deduct: 1.0,
    flt_utility_display: 0.15,
    flt_utility_deduct: 0.15,
    flt_authentication_display: 0.15,
    flt_authentication_deduct: 0.15,
    flt_authentication_intl_display: 2.4971,
    flt_authentication_intl_deduct: 2.4971,
    flt_service_display: 0.15,
    flt_service_deduct: 0.15,
  });

  // Country Master Edit Drawer (Right Sidebar)
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({
    str_country_name: "",
    str_country_code: "",
    str_calling_code: "",
    flt_marketing_rate: 0.8631,
    flt_utility_rate: 0.1150,
    flt_authentication_rate: 0.1150,
    flt_authentication_international_rate: 2.4971,
    bool_is_active: true,
  });

  // Live availability validation for Add/Edit Destination Country Right Sidebar
  const countryValidation = useMemo(() => {
    const rawName = (countryForm.str_country_name || "").trim().toLowerCase();
    const rawIso = (countryForm.str_country_code || "").trim().toUpperCase();
    const rawCall = (countryForm.str_calling_code || "").trim().replace(/[^\d]/g, "");

    if (!rawName && !rawIso && !rawCall) {
      return { status: "empty", message: "Enter country name, ISO code, and calling code to check availability." };
    }

    const combinedList = [...countries, ...metaCountries];
    const excludeId = selectedCountry ? (selectedCountry.int_country_id || selectedCountry._id) : null;

    const duplicate = combinedList.find((c) => {
      const cId = c.int_country_id || c._id;
      if (excludeId && String(cId) === String(excludeId)) return false;

      const cName = (c.str_country_name || "").trim().toLowerCase();
      const cIso = (c.str_country_code || "").trim().toUpperCase();
      const cCall = (c.str_calling_code || "").trim().replace(/[^\d]/g, "");

      if (rawName && cName === rawName) return true;
      if (rawIso && rawIso.length >= 2 && cIso === rawIso) return true;
      if (rawCall && cCall && rawCall === cCall) return true;
      return false;
    });

    if (duplicate) {
      let reason = "Destination country already exists";
      const cName = (duplicate.str_country_name || "").trim().toLowerCase();
      const cIso = (duplicate.str_country_code || "").trim().toUpperCase();
      const cCall = (duplicate.str_calling_code || "").trim().replace(/[^\d]/g, "");

      if (rawName && cName === rawName) {
        reason = `Country name '${duplicate.str_country_name}' already exists`;
      } else if (rawIso && cIso === rawIso) {
        reason = `ISO code '${duplicate.str_country_code}' already registered to ${duplicate.str_country_name}`;
      } else if (rawCall && cCall === rawCall) {
        reason = `Calling code '+${cCall}' already registered to ${duplicate.str_country_name}`;
      }

      return {
        status: "duplicate",
        duplicateCountry: duplicate,
        message: `${reason} (${duplicate.str_country_code} ${duplicate.str_calling_code || ""})`,
      };
    }

    if (rawName && rawIso.length >= 2 && rawCall) {
      return {
        status: "available",
        message: `Available: Ready to register ${countryForm.str_country_name} (+${rawCall})`,
      };
    }

    return {
      status: "typing",
      message: "Please complete country name, 2-3 char ISO code, and calling prefix.",
    };
  }, [countryForm, countries, metaCountries, selectedCountry]);

  // Live availability validation for Custom Country Form inside Account Permissions
  const customCountryValidation = useMemo(() => {
    const rawName = (customCountryForm.str_country_name || "").trim().toLowerCase();
    const rawCall = (customCountryForm.str_calling_code || "").trim().replace(/[^\d]/g, "");

    if (!rawName && !rawCall) {
      return { status: "empty", message: "Enter country name and calling prefix to check availability." };
    }

    // Check if already enabled in this account
    const alreadyEnabled = accountEnabledRates.find((r) => {
      const rCall = (r.str_calling_code || "").trim().replace(/[^\d]/g, "");
      const rName = (r.str_country_name || "").trim().toLowerCase();
      if (rawCall && rCall && rawCall === rCall) return true;
      if (rawName && rName === rawName) return true;
      return false;
    });

    if (alreadyEnabled) {
      return {
        status: "already_enabled",
        matched: alreadyEnabled,
        message: `Already Enabled: ${alreadyEnabled.str_country_name} (${alreadyEnabled.str_calling_code}) is active for this account.`,
      };
    }

    // Check if matches catalog country
    const catalogMatch = metaCountries.find((c) => {
      const cCall = (c.str_calling_code || "").trim().replace(/[^\d]/g, "");
      const cName = (c.str_country_name || "").trim().toLowerCase();
      if (rawCall && cCall && rawCall === cCall) return true;
      if (rawName && cName === rawName) return true;
      return false;
    });

    if (catalogMatch) {
      return {
        status: "catalog_match",
        matched: catalogMatch,
        message: `Matches Catalog: ${catalogMatch.str_country_name} (${catalogMatch.str_calling_code}) — custom pricing will be assigned.`,
      };
    }

    if (rawName && rawCall) {
      return {
        status: "available",
        message: `Available: Custom prefix (+${rawCall}) ready to add & enable.`,
      };
    }

    return {
      status: "typing",
      message: "Enter country name and calling code prefix.",
    };
  }, [customCountryForm, accountEnabledRates, metaCountries]);

  // Load Meta Catalog Rates
  const loadMetaRates = useCallback(async () => {
    setLoadingMetaRates(true);
    try {
      const res = await countryService.getOfficialMetaRates();
      if (res && res.success) {
        setMetaCountries(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load Meta official rates:", err);
    } finally {
      setLoadingMetaRates(false);
    }
  }, []);

  // Load Accounts & Users
  const loadAccountsAndUsers = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const [accRes, userRes, opRes] = await Promise.all([
        accountService.get({ limit: 100 }),
        userService.get({ limit: 100 }),
        operatorService.get({ limit: 50 }),
      ]);
      if (accRes && accRes.success) setAccounts(accRes.data || []);
      if (userRes && userRes.success) setUsers(userRes.data || []);
      if (opRes && opRes.success) setOperators(opRes.data || []);
    } catch (err) {
      console.error("Failed to load accounts/users:", err);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Load Destination Countries
  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await countryService.get({
        page: countryPagination.page,
        limit: countryPagination.limit,
        search: countrySearch,
      });
      if (res && res.success) {
        setCountries(res.data || []);
        setCountryPagination(res.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error("Failed to load countries:", err);
    } finally {
      setLoadingCountries(false);
    }
  }, [countryPagination.page, countryPagination.limit, countrySearch]);

  // Load Operator Wholesale Rates
  const loadOperatorRates = useCallback(async () => {
    setLoadingRates(true);
    try {
      const res = await countryService.operators.get({
        page: ratePagination.page,
        limit: ratePagination.limit,
      });
      if (res && res.success) {
        setOperatorRates(res.data || []);
        setRatePagination(res.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error("Failed to load operator rates:", err);
    } finally {
      setLoadingRates(false);
    }
  }, [ratePagination.page, ratePagination.limit]);

  const loadUserCountrySettings = useCallback(async () => {
    if (!isClient) return;
    setUserLoading(true);
    try {
      const accId = currentUser?.str_account_id || 1;
      const res = await countryService.getAccountCountrySettings(accId);
      if (res && res.success && res.data) {
        setUserAllowAllIntl(Boolean(res.data.account?.bool_allow_all_international));
        const list = res.data.enabledRates || [];
        setUserDestinations(list);
      } else {
        setUserAllowAllIntl(false);
        setUserDestinations([
          {
            int_country_id: 1,
            str_country_name: "India",
            str_country_code: "IN",
            str_calling_code: "+91",
            bool_is_enabled: true,
          },
        ]);
      }
    } catch (err) {
      console.error("Failed to load user country settings:", err);
      setUserAllowAllIntl(false);
      setUserDestinations([
        {
          int_country_id: 1,
          str_country_name: "India",
          str_country_code: "IN",
          str_calling_code: "+91",
          bool_is_enabled: true,
        },
      ]);
    } finally {
      setUserLoading(false);
    }
  }, [isClient, currentUser?.str_account_id]);

  useEffect(() => {
    if (isClient) {
      loadUserCountrySettings();
    }
  }, [isClient, loadUserCountrySettings]);

  useEffect(() => {
    if (!isClient) {
      loadMetaRates();
      loadAccountsAndUsers();
    }
  }, [isClient, loadMetaRates, loadAccountsAndUsers]);

  useEffect(() => {
    if (!isClient) {
      loadCountries();
    }
  }, [isClient, loadCountries]);

  useEffect(() => {
    if (!isClient) {
      loadOperatorRates();
    }
  }, [isClient, loadOperatorRates]);

  const handleRefresh = async () => {
    if (isClient) {
      await loadUserCountrySettings();
    } else {
      await Promise.all([loadMetaRates(), loadAccountsAndUsers(), loadCountries(), loadOperatorRates()]);
    }
  };

  // Find India baseline from Meta rates
  const indiaBaseline = useMemo(() => {
    return (
      metaCountries.find((c) => c.str_country_code === "IN") || {
        str_country_name: "India",
        str_calling_code: "+91",
        flt_marketing_rate: 0.8631,
        flt_utility_rate: 0.1150,
        flt_authentication_rate: 0.1150,
        flt_authentication_international_rate: 2.4971,
      }
    );
  }, [metaCountries]);

  // Filtered Meta Rate Table
  const filteredMetaCountries = useMemo(() => {
    if (!metaSearch) return metaCountries;
    const q = metaSearch.toLowerCase();
    return metaCountries.filter(
      (c) =>
        (c.str_country_name || "").toLowerCase().includes(q) ||
        (c.str_country_code || "").toLowerCase().includes(q) ||
        (c.str_calling_code || "").toLowerCase().includes(q)
    );
  }, [metaCountries, metaSearch]);

  // Open Country Rates & Permissions modal for an account
  const handleOpenAccountPerms = async (acc) => {
    setSelectedAcc(acc);
    setPermLoading(true);
    setIsPermModalOpen(true);
    setShowAddCustomCountry(false);
    setPermSearch("");

    try {
      const res = await countryService.getAccountCountrySettings(acc.int_account_id);
      if (res && res.success && res.data) {
        setAllowAllIntl(Boolean(res.data.account?.bool_allow_all_international));
        
        // Deduplicate enabled rates by calling code & country name
        const rawList = res.data.enabledRates || [];
        const seenKeys = new Set();
        const dedupedEnabled = [];
        for (const item of rawList) {
          const call = (item.str_calling_code || "").trim().replace(/[^\d]/g, "");
          const name = (item.str_country_name || "").trim().toLowerCase();
          const key = call ? `c_${call}` : `n_${name}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            dedupedEnabled.push(item);
          }
        }
        setAccountEnabledRates(dedupedEnabled);

        // Filter other countries to never include any that are already in enabled rates
        const enabledCallCodes = new Set(
          dedupedEnabled.map((r) => (r.str_calling_code || "").trim().replace(/[^\d]/g, "")).filter(Boolean)
        );
        const enabledNames = new Set(
          dedupedEnabled.map((r) => (r.str_country_name || "").trim().toLowerCase()).filter(Boolean)
        );
        const cleanOther = (res.data.otherCountries || []).filter((c) => {
          const cCall = (c.str_calling_code || "").trim().replace(/[^\d]/g, "");
          const cName = (c.str_country_name || "").trim().toLowerCase();
          return !enabledCallCodes.has(cCall) && !enabledNames.has(cName);
        });
        setAccountOtherCountries(cleanOther);
      } else {
        // Fallback default
        setAllowAllIntl(false);
        setAccountEnabledRates([
          {
            int_country_id: 1,
            str_country_name: "India",
            str_country_code: "IN",
            str_calling_code: "+91",
            flt_marketing_display: 1.0,
            flt_marketing_deduct: 1.0,
            flt_utility_display: 0.15,
            flt_utility_deduct: 0.15,
            flt_authentication_display: 0.15,
            flt_authentication_deduct: 0.15,
            flt_authentication_intl_display: 2.4971,
            flt_authentication_intl_deduct: 2.4971,
            flt_service_display: 0.15,
            flt_service_deduct: 0.15,
            bool_is_enabled: true,
          },
        ]);
        setAccountOtherCountries(metaCountries.filter((c) => c.str_country_code !== "IN"));
      }
    } catch (err) {
      console.error("Failed to load account country settings:", err);
    } finally {
      setPermLoading(false);
    }
  };

  // Enable a country from "Other Countries" list
  const handleEnableCountry = (c) => {
    // Prevent duplicate enablement
    const rawCall = (c.str_calling_code || "").trim().replace(/[^\d]/g, "");
    const rawName = (c.str_country_name || "").trim().toLowerCase();
    const alreadyExists = accountEnabledRates.some((r) => {
      const rCall = (r.str_calling_code || "").trim().replace(/[^\d]/g, "");
      const rName = (r.str_country_name || "").trim().toLowerCase();
      return (
        (r.int_country_id && c.int_country_id && r.int_country_id === c.int_country_id) ||
        (rawCall && rCall && rawCall === rCall) ||
        (rawName && rName && rawName === rName)
      );
    });

    if (alreadyExists) {
      Swal.fire({
        title: "Already Enabled",
        text: `${c.str_country_name} (${c.str_calling_code}) is already enabled for this account.`,
        icon: "info",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    const marketRate = c.flt_marketing_rate || 0.8631;
    const utilityRate = c.flt_utility_rate || 0.1150;
    const authRate = c.flt_authentication_rate || 0.1150;
    const authIntlRate = c.flt_authentication_international_rate || 2.4971;
    const serviceRate = c.flt_service_rate || utilityRate;

    // Both Display and Deduct (Sell) price are set to the SAME rate by default
    const newEntry = {
      int_country_id: c.int_country_id,
      str_country_name: c.str_country_name,
      str_country_code: c.str_country_code,
      str_calling_code: c.str_calling_code,
      flt_marketing_display: marketRate,
      flt_marketing_deduct: marketRate,
      flt_utility_display: utilityRate,
      flt_utility_deduct: utilityRate,
      flt_authentication_display: authRate,
      flt_authentication_deduct: authRate,
      flt_authentication_intl_display: authIntlRate,
      flt_authentication_intl_deduct: authIntlRate,
      flt_service_display: serviceRate,
      flt_service_deduct: serviceRate,
      bool_is_enabled: true,
    };

    setAccountEnabledRates((prev) => [...prev, newEntry]);
    setAccountOtherCountries((prev) => prev.filter((item) => item.int_country_id !== c.int_country_id));
  };

  // Disable / Remove a country from enabled list (except India)
  const handleDisableCountry = (index, row) => {
    if (row.str_country_code === "IN" || (row.str_country_name || "").toLowerCase() === "india") {
      Swal.fire({
        title: "India is Mandatory",
        text: "India baseline destination must remain active for all accounts.",
        icon: "info",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const removed = accountEnabledRates[index];
    setAccountEnabledRates((prev) => prev.filter((_, i) => i !== index));
    if (removed) {
      setAccountOtherCountries((prev) => [...prev, removed]);
    }
  };

  // Update a rate value in the Enabled Countries table
  const handleRateInputChange = (index, field, value) => {
    setAccountEnabledRates((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: Number(value) };
      return copy;
    });
  };

  // Add custom country prefix & rates
  const handleAddCustomCountry = (e) => {
    e.preventDefault();
    if (!customCountryForm.str_country_name || !customCountryForm.str_calling_code) {
      Swal.fire("Missing Fields", "Please specify country name and calling code prefix.", "warning");
      return;
    }

    if (customCountryValidation.status === "already_enabled") {
      Swal.fire({
        title: "Already Enabled",
        text: `This destination (${customCountryValidation.matched?.str_country_name || "Country"}) is already enabled for this account. Duplicate rows are not allowed.`,
        icon: "warning",
      });
      return;
    }

    const rawCall = customCountryForm.str_calling_code.trim().replace(/[^\d]/g, "");
    const formattedCalling = `+${rawCall}`;
    const rawName = customCountryForm.str_country_name.trim();

    // Check if it matches a catalog country to reuse canonical ID and ISO code
    const catalogMatch = metaCountries.find((c) => {
      const cCall = (c.str_calling_code || "").trim().replace(/[^\d]/g, "");
      const cName = (c.str_country_name || "").trim().toLowerCase();
      return (rawCall && cCall === rawCall) || (rawName && cName === rawName.toLowerCase());
    });

    const newCustom = {
      int_country_id: catalogMatch ? catalogMatch.int_country_id : Date.now(),
      str_country_name: catalogMatch ? catalogMatch.str_country_name : rawName,
      str_country_code: catalogMatch ? catalogMatch.str_country_code : rawName.substring(0, 3).toUpperCase(),
      str_calling_code: catalogMatch ? catalogMatch.str_calling_code : formattedCalling,
      flt_marketing_display: Number(customCountryForm.flt_marketing_display),
      flt_marketing_deduct: Number(customCountryForm.flt_marketing_deduct),
      flt_utility_display: Number(customCountryForm.flt_utility_display),
      flt_utility_deduct: Number(customCountryForm.flt_utility_deduct),
      flt_authentication_display: Number(customCountryForm.flt_authentication_display),
      flt_authentication_deduct: Number(customCountryForm.flt_authentication_deduct),
      flt_authentication_intl_display: Number(customCountryForm.flt_authentication_intl_display),
      flt_authentication_intl_deduct: Number(customCountryForm.flt_authentication_intl_deduct),
      flt_service_display: Number(customCountryForm.flt_service_display ?? 0.15),
      flt_service_deduct: Number(customCountryForm.flt_service_deduct ?? 0.15),
      bool_is_enabled: true,
    };

    setAccountEnabledRates((prev) => [...prev, newCustom]);
    if (catalogMatch) {
      setAccountOtherCountries((prev) =>
        prev.filter((c) => c.int_country_id !== catalogMatch.int_country_id)
      );
    }
    setShowAddCustomCountry(false);
    setCustomCountryForm({
      str_country_name: "",
      str_calling_code: "",
      flt_marketing_display: 1.0,
      flt_marketing_deduct: 1.0,
      flt_utility_display: 0.15,
      flt_utility_deduct: 0.15,
      flt_authentication_display: 0.15,
      flt_authentication_deduct: 0.15,
      flt_authentication_intl_display: 2.4971,
      flt_authentication_intl_deduct: 2.4971,
      flt_service_display: 0.15,
      flt_service_deduct: 0.15,
    });
  };

  // Save Country Rates & Permissions for Account
  const handleSaveAccountCountryRates = async () => {
    if (!selectedAcc) return;
    setIsSavingPerms(true);

    try {
      const allowedCodes = accountEnabledRates.map((r) => r.str_country_code || r.str_country_name);
      if (!allowedCodes.includes("IN")) allowedCodes.push("IN");

      const payload = {
        bool_allow_all_international: allowAllIntl,
        allowed_countries: allowedCodes,
        rates: accountEnabledRates,
      };

      const res = await countryService.saveAccountCountrySettings(selectedAcc.int_account_id, payload);

      if (res && res.success !== false) {
        await Swal.fire({
          title: "Country Rates Saved!",
          text: `Permissions and custom pricing updated for ${selectedAcc.str_account_code || `ACC${String(selectedAcc.int_account_id).padStart(6, "0")}`}.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        setIsPermModalOpen(false);
        loadAccountsAndUsers();
      } else {
        Swal.fire("Error", res?.error || "Failed to save country rates", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to save country rates", "error");
    } finally {
      setIsSavingPerms(false);
    }
  };

  // Open Account User Messaging Policy Settings Modal
  const handleOpenUserSettings = async (acc) => {
    setSelectedUserSettingsAcc(acc);
    setIsUserSettingsModalOpen(true);
    setLoadingUserSettings(true);
    try {
      const accId = acc.int_account_id ?? acc._id;
      const res = await countryService.getAccountUserSettings(accId);
      const d = (res && res.data && typeof res.data === "object" && !res.data.data) ? res.data : (res?.data?.data || res?.data || res);
      if (d && (d.bool_deduct_on_failed !== undefined || d.int_account_id != null || res?.success)) {
        setUserSettingsForm({
          bool_deduct_on_failed: d.bool_deduct_on_failed !== false,
          bool_show_credits: d.bool_show_credits !== false,
          bool_failed_cap_enabled: Boolean(d.bool_failed_cap_enabled),
          num_failed_cap_percent: Number(d.num_failed_cap_percent || 0),
          bool_refund_failed_credit: Boolean(d.bool_refund_failed_credit),
          num_refund_delay_hours: Number(d.num_refund_delay_hours != null ? d.num_refund_delay_hours : 24),
        });
      } else {
        setUserSettingsForm({
          bool_deduct_on_failed: acc.bool_deduct_on_failed !== false,
          bool_show_credits: acc.bool_show_credits !== false,
          bool_failed_cap_enabled: Boolean(acc.bool_failed_cap_enabled),
          num_failed_cap_percent: Number(acc.num_failed_cap_percent || 0),
          bool_refund_failed_credit: Boolean(acc.bool_refund_failed_credit),
          num_refund_delay_hours: Number(acc.num_refund_delay_hours != null ? acc.num_refund_delay_hours : 24),
        });
      }
    } catch (err) {
      console.warn("Failed to load user settings, fallback to defaults:", err);
      setUserSettingsForm({
        bool_deduct_on_failed: acc.bool_deduct_on_failed !== false,
        bool_show_credits: acc.bool_show_credits !== false,
        bool_failed_cap_enabled: Boolean(acc.bool_failed_cap_enabled),
        num_failed_cap_percent: Number(acc.num_failed_cap_percent || 0),
        bool_refund_failed_credit: Boolean(acc.bool_refund_failed_credit),
        num_refund_delay_hours: Number(acc.num_refund_delay_hours != null ? acc.num_refund_delay_hours : 24),
      });
    } finally {
      setLoadingUserSettings(false);
    }
  };

  // Save Account User Messaging Policy Settings
  const handleSaveUserSettings = async () => {
    if (!selectedUserSettingsAcc) return;
    setSavingUserSettings(true);
    try {
      const payload = {
        bool_deduct_on_failed: userSettingsForm.bool_deduct_on_failed,
        bool_show_credits: userSettingsForm.bool_show_credits,
        bool_failed_cap_enabled: userSettingsForm.bool_failed_cap_enabled,
        num_failed_cap_percent: Math.min(100, Math.max(0, Number(userSettingsForm.num_failed_cap_percent || 0))),
        bool_refund_failed_credit: userSettingsForm.bool_refund_failed_credit,
        num_refund_delay_hours: Math.max(0, Number(userSettingsForm.num_refund_delay_hours || 0)),
      };

      const accId = selectedUserSettingsAcc.int_account_id ?? selectedUserSettingsAcc._id;
      const res = await countryService.saveAccountUserSettings(accId, payload);
      
      const isOk = Boolean(
        res?.success === true ||
        res?.data?.success === true ||
        (res && !res.error && res.status !== 500 && (res.message || res.data || res.int_account_id))
      );

      if (isOk) {
        setAccounts((prev) =>
          prev.map((a) =>
            (a.int_account_id === selectedUserSettingsAcc.int_account_id || a._id === selectedUserSettingsAcc._id)
              ? { ...a, ...payload }
              : a
          )
        );

        Swal.fire({
          icon: "success",
          title: "Settings Saved",
          text: `User settings for account ${selectedUserSettingsAcc.str_company_name || selectedUserSettingsAcc.str_account_code} saved successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        setIsUserSettingsModalOpen(false);
      } else {
        throw new Error(res?.error || res?.data?.error || res?.message || "Failed to update settings");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err.message || "An error occurred while saving user settings.",
      });
    } finally {
      setSavingUserSettings(false);
    }
  };

  // Open Right Sidebar Drawer to add a new destination country
  const handleOpenAddCountry = () => {
    setSelectedCountry(null);
    setCountryForm({
      str_country_name: "",
      str_country_code: "",
      str_calling_code: "+",
      flt_marketing_rate: 0.8631,
      flt_utility_rate: 0.1150,
      flt_authentication_rate: 0.1150,
      flt_authentication_international_rate: 0.0,
      flt_service_rate: 0.1150,
      bool_is_active: true,
    });
    setIsCountryModalOpen(true);
  };

  // Open Right Sidebar Drawer to edit an existing destination country
  const handleOpenEditCountry = (c) => {
    setSelectedCountry(c);
    setCountryForm({
      str_country_name: c.str_country_name || "",
      str_country_code: c.str_country_code || "",
      str_calling_code: c.str_calling_code || "",
      flt_marketing_rate: c.flt_marketing_rate != null ? c.flt_marketing_rate : 0.8631,
      flt_utility_rate: c.flt_utility_rate != null ? c.flt_utility_rate : 0.1150,
      flt_authentication_rate: c.flt_authentication_rate != null ? c.flt_authentication_rate : 0.1150,
      flt_authentication_international_rate: c.flt_authentication_international_rate != null ? c.flt_authentication_international_rate : 0.0,
      flt_service_rate: c.flt_service_rate != null ? c.flt_service_rate : 0.1150,
      bool_is_active: c.bool_is_active !== false,
    });
    setIsCountryModalOpen(true);
  };

  // Save / Update destination country in master database
  const handleSaveCountry = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (countryValidation.status === "duplicate") {
      Swal.fire({
        title: "Duplicate Country",
        text: countryValidation.message,
        icon: "warning",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    try {
      if (selectedCountry) {
        const id = selectedCountry.int_country_id || selectedCountry._id;
        await countryService.update(id, countryForm);
        Swal.fire({
          title: "Country Updated",
          text: `${countryForm.str_country_name} baseline rates updated successfully.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        await countryService.create(countryForm);
        Swal.fire({
          title: "Country Added",
          text: `${countryForm.str_country_name} added to destination catalog.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
        });
      }
      setIsCountryModalOpen(false);
      loadCountries();
      loadMetaRates();
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to save country", "error");
    }
  };

  // Filtered lists inside Permissions modal
  const modalFilteredEnabled = useMemo(() => {
    if (!permSearch) return accountEnabledRates;
    const q = permSearch.toLowerCase();
    return accountEnabledRates.filter(
      (r) =>
        (r.str_country_name || "").toLowerCase().includes(q) ||
        (r.str_calling_code || "").toLowerCase().includes(q) ||
        (r.str_country_code || "").toLowerCase().includes(q)
    );
  }, [accountEnabledRates, permSearch]);

  const modalFilteredOther = useMemo(() => {
    if (!permSearch) return accountOtherCountries;
    const q = permSearch.toLowerCase();
    return accountOtherCountries.filter(
      (c) =>
        (c.str_country_name || "").toLowerCase().includes(q) ||
        (c.str_calling_code || "").toLowerCase().includes(q) ||
        (c.str_country_code || "").toLowerCase().includes(q)
    );
  }, [accountOtherCountries, permSearch]);

  // Destination Countries List & Pagination
  const filteredCountries = useMemo(() => {
    const list = countries.length > 0 ? countries : metaCountries;
    if (!countrySearch) return list;
    const q = countrySearch.toLowerCase();
    return list.filter(
      (c) =>
        (c.str_country_name || "").toLowerCase().includes(q) ||
        (c.str_country_code || "").toLowerCase().includes(q) ||
        (c.str_calling_code || "").toLowerCase().includes(q)
    );
  }, [countries, metaCountries, countrySearch]);

  const pagedCountries = useMemo(() => {
    const start = (countryPagination.page - 1) * countryPagination.limit;
    return filteredCountries.slice(start, start + countryPagination.limit);
  }, [filteredCountries, countryPagination.page, countryPagination.limit]);

  // Account List Filter
  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts;
    const q = accountSearch.toLowerCase();
    return accounts.filter((acc) => {
      const code = (acc.str_account_code || `ACC${String(acc.int_account_id)}`).toLowerCase();
      const company = (acc.str_company_name || "").toLowerCase();
      const user = users.find((u) => String(u.str_account_id) === String(acc.int_account_id));
      const username = (user?.str_username || "").toLowerCase();
      const name = (user?.str_name || "").toLowerCase();
      return code.includes(q) || company.includes(q) || username.includes(q) || name.includes(q);
    });
  }, [accounts, users, accountSearch]);

  // Account User Policy Settings Filtering & Metrics
  const filteredUserSettingsAccounts = useMemo(() => {
    if (!userSettingsSearch) return accounts;
    const q = userSettingsSearch.toLowerCase();
    return accounts.filter((acc) => {
      const code = (acc.str_account_code || `ACC${String(acc.int_account_id)}`).toLowerCase();
      const company = (acc.str_company_name || "").toLowerCase();
      const user = users.find((u) => String(u.str_account_id) === String(acc.int_account_id));
      const username = (user?.str_username || "").toLowerCase();
      const name = (user?.str_name || "").toLowerCase();
      return code.includes(q) || company.includes(q) || username.includes(q) || name.includes(q);
    });
  }, [accounts, users, userSettingsSearch]);

  const userSettingsStats = useMemo(() => {
    const total = accounts.length;
    const deductOnFailedActive = accounts.filter((a) => a.bool_deduct_on_failed !== false).length;
    const showCreditsActive = accounts.filter((a) => a.bool_show_credits !== false).length;
    const failedCapActive = accounts.filter((a) => a.bool_failed_cap_enabled).length;
    const refundFailedActive = accounts.filter((a) => a.bool_refund_failed_credit).length;
    return { total, deductOnFailedActive, showCreditsActive, failedCapActive, refundFailedActive };
  }, [accounts]);

  // Regular user destination filtering
  const filteredUserDestinations = useMemo(() => {
    return userDestinations.filter((c) => {
      if (userSearch) {
        const q = userSearch.toLowerCase();
        const name = (c.str_country_name || "").toLowerCase();
        const code = (c.str_country_code || "").toLowerCase();
        const call = (c.str_calling_code || "").toLowerCase();
        if (!name.includes(q) && !code.includes(q) && !call.includes(q)) return false;
      }
      if (userStatusFilter === "active" && c.bool_is_enabled === false) return false;
      if (userStatusFilter === "inactive" && c.bool_is_enabled !== false) return false;
      return true;
    });
  }, [userDestinations, userSearch, userStatusFilter]);

  // ── USER ROLE VIEW (No Pricing, Active/Deactive Status, International Status, Contact Admin Notice) ──
  if (isClient) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto pb-12">
        {/* Header Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
                Allowed Destination Countries
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                Destinations and calling prefixes authorized for sending WhatsApp messages from your account
              </p>
            </div>
            <button
              onClick={loadUserCountrySettings}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer whitespace-nowrap self-start sm:self-auto"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-2 ${userLoading ? "animate-spin" : ""}`} />
              Refresh Countries
            </button>
          </div>
        </div>

        {/* International Calling Status Card */}
        <div className={`p-6 rounded-2xl border transition-all shadow-sm ${
          userAllowAllIntl
            ? "bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/50 dark:from-emerald-950/30 dark:via-gray-800 dark:to-teal-950/20 border-emerald-300 dark:border-emerald-700/60"
            : "bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/50 dark:from-blue-950/30 dark:via-gray-800 dark:to-indigo-950/20 border-blue-200 dark:border-blue-700/60"
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                userAllowAllIntl
                  ? "bg-emerald-600 text-white"
                  : "bg-blue-600 text-white"
              }`}>
                <GlobeAmericasIcon className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    International Messaging: {userAllowAllIntl ? "ACTIVE & UNRESTRICTED" : "RESTRICTED TO WHITELIST"}
                  </h3>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                    userAllowAllIntl
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200 border border-amber-300 dark:border-amber-700"
                  }`}>
                    {userAllowAllIntl ? "● All International Destinations Allowed" : "● Whitelisted Countries Only"}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1 max-w-2xl leading-relaxed">
                  {userAllowAllIntl
                    ? "Your WhatsApp Business Account is permitted to send outgoing messages to all international phone numbers globally without country restrictions."
                    : "International delivery permissions are restricted. Your account can ONLY send messages to recipient phone numbers with calling prefixes corresponding to the active destinations listed below."}
                </p>
              </div>
            </div>
            <div className="text-right self-start sm:self-center flex-shrink-0">
              <span className="text-xs text-gray-400 font-mono block">
                Account ID: ACC{String(currentUser?.str_account_id || 1).padStart(6, "0")}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Administrator Callout Banner */}
        <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 border border-amber-200/80 dark:border-amber-800/60 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
              <InformationCircleIcon className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">
                Need to add a destination country or enable international messaging?
              </h4>
              <p className="text-xs text-amber-800/90 dark:text-amber-300/90 mt-0.5">
                Destination countries and global messaging permissions are managed centrally by your administrator. Please contact admin to request additional country routes.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              Swal.fire({
                title: "Contact Platform Administrator",
                html: `
                  <div style="font-size:13px;color:#334155;line-height:1.7;text-align:left;">
                    <p style="margin-bottom:10px;">
                      To activate new international destination countries or enable worldwide messaging for Account <b>ACC${String(currentUser?.str_account_id || 1).padStart(6, "0")}</b>, please reach out to your administrator:
                    </p>
                    <div style="background:#f8fafc;padding:12px 16px;border-radius:12px;border:1px solid #e2e8f0;font-family:monospace;font-size:12px;margin-bottom:10px;">
                      <div><b>Admin Email:</b> admin@digiforest.io</div>
                      <div style="margin-top:4px;"><b>Support Desk:</b> support@digiforest.io</div>
                      <div style="margin-top:4px;"><b>WhatsApp Support:</b> +91 99999 99999</div>
                    </div>
                    <p style="font-size:11px;color:#64748b;">
                      Please specify your requested country names and anticipated message volume in your ticket.
                    </p>
                  </div>
                `,
                icon: "info",
                confirmButtonColor: "#0f172a",
                confirmButtonText: "Got It",
              });
            }}
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-700 hover:to-orange-700 text-xs shadow-xs transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            Contact Administrator
          </button>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search country name, ISO code, or calling code (+91, +1)..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Status Filter */}
            <div className="relative min-w-[160px]">
              <select
                value={userStatusFilter}
                onChange={(e) => setUserStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Deactive Only</option>
              </select>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold px-2">
              {filteredUserDestinations.length} destination{filteredUserDestinations.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Countries Table (NO PRICING SHOWN) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {userLoading ? (
            <div className="py-16 text-center">
              <ArrowPathIcon className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-3" />
              <p className="text-gray-500 dark:text-gray-400 text-sm">Loading destinations...</p>
            </div>
          ) : filteredUserDestinations.length === 0 ? (
            <div className="py-16 px-4 text-center">
              <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                <GlobeAmericasIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                No Destination Countries Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm max-w-md mx-auto mb-4">
                {userSearch
                  ? "No destination countries match your search keyword."
                  : "No countries have been activated for your account yet. Contact your administrator to activate destination countries."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                      #
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Destination Country
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Calling Code
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Coverage Type
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredUserDestinations.map((c, idx) => {
                    const isIndia = c.str_country_code === "IN" || (c.str_country_name || "").toLowerCase() === "india";
                    const isEnabled = c.bool_is_enabled !== false;
                    const initial = (c.str_country_name || "C").charAt(0).toUpperCase();

                    return (
                      <tr
                        key={c.int_country_id || idx}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                          {idx + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-xs ${
                              isIndia ? "bg-orange-500" : "bg-blue-600"
                            }`}>
                              {initial}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                {c.str_country_name}
                              </div>
                              <div className="text-[11px] text-gray-400 font-mono">
                                ISO: {c.str_country_code || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                            {c.str_calling_code || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isIndia
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                              : "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                          }`}>
                            {isIndia ? "Domestic (Primary)" : "International"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEnabled ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              ● Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                              ● Deactive
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* ── Top Section 1: Default Meta Rates (India Baseline: +91) & Catalog (Image 1) ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-400">
              Default Meta Rates (India Baseline: +91)
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5">
              Automatic prefix detection applies exact Meta pricing per country calling code (Effective October 1, 2026)
            </p>
          </div>
          <button
            onClick={() => setShowMetaRatesTable(!showMetaRatesTable)}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-sm cursor-pointer whitespace-nowrap self-start sm:self-auto"
          >
            {showMetaRatesTable ? "Hide Country Rates Table" : "View Official Country Rates Table"}
          </button>
        </div>

        {/* 4 Category Baseline Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-gray-50/80 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-center">
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              ₹{Number(indiaBaseline.flt_marketing_rate || 0.8631).toFixed(4)}
            </div>
            <div className="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">
              Marketing
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">India (+91)</div>
          </div>

          <div className="p-4 rounded-xl bg-gray-50/80 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-center">
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              ₹{Number(indiaBaseline.flt_utility_rate || 0.1150).toFixed(4)}
            </div>
            <div className="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">
              Utility
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">India (+91)</div>
          </div>

          <div className="p-4 rounded-xl bg-gray-50/80 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-center">
            <div className="text-2xl sm:text-3xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
              ₹{Number(indiaBaseline.flt_authentication_rate || 0.1150).toFixed(4)}
            </div>
            <div className="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">
              Authentication
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">India (+91)</div>
          </div>

          <div className="p-4 rounded-xl bg-gray-50/80 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-center">
            <div className="text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400 font-mono">
              ₹{Number(indiaBaseline.flt_authentication_international_rate || 2.4971).toFixed(4)}
            </div>
            <div className="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-200 mt-1">
              Auth-International
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">India (+91)</div>
          </div>
        </div>

        {/* Collapsible Official Country-Wise Meta Rates Table */}
        {showMetaRatesTable && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4 animate-in fade-in duration-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                Official Country-Wise Meta Rates (Effective October 1, 2026)
              </h3>
              <div className="relative w-full sm:w-64">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter country or code..."
                  value={metaSearch}
                  onChange={(e) => setMetaSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto max-h-96 border border-gray-200 dark:border-gray-700 rounded-xl">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/60 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Country / Market</th>
                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Calling Code</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Marketing (₹)</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Utility (₹)</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Authentication (₹)</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Auth-International (₹)</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Service (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {loadingMetaRates ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent mb-1" />
                        <div>Loading Meta catalog rates...</div>
                      </td>
                    </tr>
                  ) : (
                    filteredMetaCountries.map((c) => (
                      <tr key={c.str_country_code} className="hover:bg-blue-50/50 dark:hover:bg-gray-700/40">
                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">
                          {c.str_country_name}
                        </td>
                      <td className="px-4 py-2 font-mono text-blue-600 dark:text-blue-400">
                        {c.str_calling_code}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">₹{Number(c.flt_marketing_rate || 0).toFixed(4)}</td>
                      <td className="px-4 py-2 text-right font-mono">₹{Number(c.flt_utility_rate || 0).toFixed(4)}</td>
                      <td className="px-4 py-2 text-right font-mono">₹{Number(c.flt_authentication_rate || 0).toFixed(4)}</td>
                      <td className="px-4 py-2 text-right font-mono text-indigo-600 dark:text-indigo-400">
                        {c.flt_authentication_international_rate ? `₹${Number(c.flt_authentication_international_rate).toFixed(4)}` : "n/a"}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">₹{Number(c.flt_service_rate || 0).toFixed(4)}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Main Section: Countries & Messaging Rates Hub (Image 4) ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-1">
              Countries & Messaging Rates
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Manage international destination countries, telecom wholesale purchase rates, and custom client pricing overrides
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all text-xs sm:text-sm cursor-pointer shadow-sm"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => {
                if (activeTab === "accounts" && accounts.length > 0) {
                  handleOpenAccountPerms(accounts[0]);
                } else {
                  handleOpenAddCountry();
                }
              }}
              className="inline-flex items-center px-4 py-2.5 bg-[#008069] hover:bg-[#006e5a] text-white font-semibold rounded-xl transition-all text-xs sm:text-sm cursor-pointer shadow-sm"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              + Add Country
            </button>
          </div>
        </div>

        {/* 4 Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50/70 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
            <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">{metaCountries.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Registered Destinations</div>
          </div>
          <div className="bg-gray-50/70 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
            <div className="text-2xl font-bold text-blue-600 font-mono">{accounts.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Active Client Accounts</div>
          </div>
          <div className="bg-gray-50/70 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
            <div className="text-2xl font-bold text-purple-600 font-mono">{operatorRates.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Carrier Wholesale Routes</div>
          </div>
          <div className="bg-gray-50/70 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
            <div className="text-2xl font-bold text-green-600 font-mono">{operators.length}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Active Telecom Operators</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs (Matching Image 4) */}
      <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab("destinations")}
          className={`inline-flex items-center px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "destinations"
              ? "bg-[#007AFF] text-white shadow-md shadow-blue-500/20"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          <GlobeAmericasIcon className="w-4 h-4 mr-1.5" />
          1. Destination Countries
        </button>
        
        <button
          onClick={() => setActiveTab("accounts")}
          className={`px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "accounts"
              ? "bg-[#007AFF] text-white shadow-md shadow-blue-500/20"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          💳 2. Account Custom Pricing
        </button>

        <button
          onClick={() => setActiveTab("user-settings")}
          className={`inline-flex items-center px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all cursor-pointer ${
            activeTab === "user-settings"
              ? "bg-[#007AFF] text-white shadow-md shadow-blue-500/20"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
          }`}
        >
          <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
          ⚙️ 3. Account User Settings
        </button>
      </div>

      {/* ── TAB 1: Destination Countries ── */}
      {activeTab === "destinations" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search destination country or code..."
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setCountryPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className="w-full pl-11 pr-4 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Rows:</span>
              <select
                value={countryPagination.limit}
                onChange={(e) =>
                  setCountryPagination((prev) => ({
                    ...prev,
                    limit: Number(e.target.value),
                    page: 1,
                  }))
                }
                className="px-3 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">#</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">Destination Country</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">Calling Code</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">ISO Code</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Marketing</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Utility</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Authentication</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Auth-Intl</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Service</th>
                    <th className="px-6 py-3.5 text-center font-semibold text-gray-600 dark:text-gray-300">Status</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {loadingCountries ? (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-gray-400">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mb-2" />
                        <div>Loading destination countries...</div>
                      </td>
                    </tr>
                  ) : pagedCountries.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-gray-400">
                        No destination countries found matching your search.
                      </td>
                    </tr>
                  ) : (
                    pagedCountries.map((row, index) => {
                      const rowNumber = (countryPagination.page - 1) * countryPagination.limit + index + 1;
                      const code = row.str_country_code || "XX";
                    return (
                      <tr key={row._id || row.int_country_id || index} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40">
                        <td className="px-6 py-4 text-gray-400 font-mono text-xs">{rowNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 flex items-center justify-center font-bold text-xs uppercase font-mono border border-blue-200 dark:border-blue-800">
                              {code.substring(0, 2)}
                            </span>
                            <div>
                              <div className="font-bold text-gray-900 dark:text-white">{row.str_country_name}</div>
                              <div className="text-[11px] text-gray-400 font-mono">ID #{row.int_country_id || index + 1}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-mono text-blue-600 dark:text-blue-400 font-semibold whitespace-nowrap">
                          {row.str_calling_code || "—"}
                        </td>
                        <td className="px-6 py-4 font-mono text-gray-700 dark:text-gray-300 uppercase whitespace-nowrap">
                          {code}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ₹{Number(row.flt_marketing_rate || 0).toFixed(4)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ₹{Number(row.flt_utility_rate || 0).toFixed(4)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ₹{Number(row.flt_authentication_rate || 0).toFixed(4)}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                          {row.flt_authentication_international_rate ? `₹${Number(row.flt_authentication_international_rate).toFixed(4)}` : "—"}
                        </td>
                        <td className="px-6 py-4 text-right font-mono text-teal-600 dark:text-teal-400 font-semibold whitespace-nowrap">
                          ₹{Number(row.flt_service_rate != null ? row.flt_service_rate : (row.flt_utility_rate || 0)).toFixed(4)}
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            row.bool_is_active !== false
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {row.bool_is_active !== false ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleOpenEditCountry(row)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
                            title="Edit destination country"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
              <div>
                Showing{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {filteredCountries.length === 0
                    ? 0
                    : (countryPagination.page - 1) * countryPagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {Math.min(countryPagination.page * countryPagination.limit, filteredCountries.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {filteredCountries.length}
                </span>{" "}
                destinations
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={countryPagination.page <= 1}
                  onClick={() =>
                    setCountryPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <span className="px-2 font-mono">
                  Page {countryPagination.page} of{" "}
                  {Math.max(1, Math.ceil(filteredCountries.length / countryPagination.limit))}
                </span>
                <button
                  disabled={
                    countryPagination.page >=
                    Math.ceil(filteredCountries.length / countryPagination.limit)
                  }
                  onClick={() =>
                    setCountryPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

     

      {/* ── TAB 3: Account-Wise Country Permissions & Custom Pricing ── */}
      {activeTab === "accounts" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="relative max-w-md">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search account code, company name, or username..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">Client Account</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">User / Manager</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">International Permission</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">Enabled Countries</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {loadingAccounts ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-400">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mb-2" />
                        <div>Loading client accounts...</div>
                      </td>
                    </tr>
                  ) : filteredAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-400">
                        No client accounts found.
                      </td>
                    </tr>
                  ) : (
                    filteredAccounts.map((acc) => {
                      const accCode = acc.str_account_code || `ACC${String(acc.int_account_id).padStart(6, "0")}`;
                    const matchedUser =
                      acc.assigned_user ||
                      users.find((u) => String(u.str_account_id) === String(acc.int_account_id));
                    const isAllIntl = Boolean(acc.bool_allow_all_international);
                    const countryCount = Array.isArray(acc.allowed_countries) ? acc.allowed_countries.length : 1;

                    return (
                      <tr key={acc.int_account_id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-bold text-gray-900 dark:text-white font-mono">{accCode}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {acc.str_company_name || "Client Account"}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {matchedUser ? (
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white">{matchedUser.str_name || matchedUser.str_username}</div>
                              <div className="text-xs text-blue-600 dark:text-blue-400 font-mono">@{matchedUser.str_username}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Unassigned</span>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {isAllIntl ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              🌍 All International Allowed
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                              🔒 India Baseline Only (Restricted)
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-700 dark:text-gray-300">
                          {isAllIntl ? "All 47+ Countries" : `${Math.max(1, countryCount)} Enabled (India Default)`}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                          <button
                            onClick={() => handleOpenAccountPerms(acc)}
                            className="inline-flex items-center px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold text-xs hover:from-green-700 hover:to-blue-700 shadow-sm cursor-pointer transition-all"
                          >
                            <AdjustmentsHorizontalIcon className="w-4 h-4 mr-1.5" />
                            Country Rates & Permissions
                          </button>
                          <button
                            onClick={() => handleOpenUserSettings(acc)}
                            className="inline-flex items-center px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-xs hover:from-purple-700 hover:to-indigo-700 shadow-sm cursor-pointer transition-all"
                            title="Configure User Policy Settings"
                          >
                            <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                            ⚙️ User Settings
                          </button>
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: Account-Wise User Messaging & Delivery Settings ── */}
      {activeTab === "user-settings" && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Top Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Client Accounts</span>
                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Cog6ToothIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">{userSettingsStats.total}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Configured for tenant messaging</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-800/40 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Deduct On Failed</span>
                <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <CheckCircleIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{userSettingsStats.deductOnFailedActive}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Active delivery deductions</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-purple-200 dark:border-purple-800/40 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">Failed Cap Active</span>
                <div className="w-8 h-8 rounded-xl bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                  <ShieldCheckIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-purple-600 dark:text-purple-400 mt-1">{userSettingsStats.failedCapActive}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Masking ratio enabled</div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-amber-200 dark:border-amber-800/40 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">Auto-Refund Active</span>
                <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                  <ClockIcon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">{userSettingsStats.refundFailedActive}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">Automated failed credit refund</div>
            </div>
          </div>

          {/* Search bar */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="relative max-w-md">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search account code, company name, or username..."
                value={userSettingsSearch}
                onChange={(e) => setUserSettingsSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Accounts Policy Table */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs sm:text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">Client Account</th>
                    <th className="px-6 py-3.5 text-left font-semibold text-gray-600 dark:text-gray-300">User / Manager</th>
                    <th className="px-6 py-3.5 text-center font-semibold text-gray-600 dark:text-gray-300">Deduct On Failed</th>
                    <th className="px-6 py-3.5 text-center font-semibold text-gray-600 dark:text-gray-300">Show Credits</th>
                    <th className="px-6 py-3.5 text-center font-semibold text-gray-600 dark:text-gray-300">Failed Cap</th>
                    <th className="px-6 py-3.5 text-center font-semibold text-gray-600 dark:text-gray-300">Refund Failed Credit</th>
                    <th className="px-6 py-3.5 text-right font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {loadingAccounts ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mb-2" />
                        <div>Loading client accounts...</div>
                      </td>
                    </tr>
                  ) : filteredUserSettingsAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-400">
                        No client accounts found.
                      </td>
                    </tr>
                  ) : (
                    filteredUserSettingsAccounts.map((acc) => {
                      const accCode = acc.str_account_code || `ACC${String(acc.int_account_id).padStart(6, "0")}`;
                      const matchedUser =
                        acc.assigned_user ||
                        users.find((u) => String(u.str_account_id) === String(acc.int_account_id));
                      const deductOnFailed = acc.bool_deduct_on_failed !== false;
                      const showCredits = acc.bool_show_credits !== false;
                      const failedCap = Boolean(acc.bool_failed_cap_enabled);
                      const capPct = Number(acc.num_failed_cap_percent || 0);
                      const refundCredit = Boolean(acc.bool_refund_failed_credit);
                      const delayHrs = Number(acc.num_refund_delay_hours != null ? acc.num_refund_delay_hours : 24);

                      return (
                        <tr key={acc.int_account_id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-bold text-gray-900 dark:text-white font-mono">{accCode}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {acc.str_company_name || "Client Account"}
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            {matchedUser ? (
                              <div>
                                <div className="font-semibold text-gray-900 dark:text-white">{matchedUser.str_name || matchedUser.str_username}</div>
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-mono">@{matchedUser.str_username}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Unassigned</span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {deductOnFailed ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                                Enabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                Disabled (No Deduct)
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {showCredits ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                Visible
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                Hidden
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {failedCap ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                                Active ({capPct}%)
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                Disabled
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {refundCredit ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                Active ({delayHrs}h delay)
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                Disabled
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleOpenUserSettings(acc)}
                              className="inline-flex items-center px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-xs hover:from-purple-700 hover:to-indigo-700 shadow-sm cursor-pointer transition-all"
                            >
                              <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                              Configure Settings
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Country Rates & Permissions Modal Dialog (Matching Images 2 & 3) ── */}
      {isPermModalOpen && selectedAcc && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-5xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50/90 to-white dark:from-gray-900/60 dark:to-gray-800">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Country Rates & Permissions: <span className="text-blue-600 dark:text-blue-400 font-mono">{selectedAcc.str_company_name || selectedAcc.str_account_code || `ACC${String(selectedAcc.int_account_id).padStart(6, "0")}`}</span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Enable/disable specific country prefixes and set custom pricing rates (INR)
                </p>
              </div>
              <button
                onClick={() => setIsPermModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            {permLoading ? (
              <div className="p-16 flex flex-col items-center justify-center space-y-3 flex-1">
                <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent" />
                <p className="text-xs text-gray-500 dark:text-gray-400">Loading country settings...</p>
              </div>
            ) : (
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-6">
              
              {/* Allow All International Destinations Banner Card */}
              <div className="p-4 sm:p-5 rounded-2xl bg-blue-50/80 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <label className="flex items-start sm:items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allowAllIntl}
                    onChange={(e) => setAllowAllIntl(e.target.checked)}
                    className="mt-0.5 sm:mt-0 rounded border-blue-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-bold text-blue-900 dark:text-blue-300 block">
                      Allow All International Destinations
                    </span>
                    <span className="text-xs text-blue-700 dark:text-blue-400 mt-0.5 block">
                      When enabled, all non-India numbers are permitted automatically
                    </span>
                  </div>
                </label>

                <div className="flex items-center gap-2.5">
                  <button
                    onClick={() => setShowAddCustomCountry(!showAddCustomCountry)}
                    className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-semibold shadow-sm cursor-pointer transition-all whitespace-nowrap"
                  >
                    <PlusIcon className="w-4 h-4 mr-1.5" />
                    + Add Country
                  </button>

                  <div className="relative w-48 sm:w-56">
                    <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search country or code..."
                      value={permSearch}
                      onChange={(e) => setPermSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Collapsible "+ Add Custom Country Prefix & Rates" Form (Image 3) */}
              {showAddCustomCountry && (
                <div className="p-5 rounded-2xl bg-green-50/70 dark:bg-green-950/20 border border-green-200 dark:border-green-800/40 space-y-4 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between border-b border-green-200 dark:border-green-800/40 pb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-green-800 dark:text-green-300">
                      + Add Custom Country Prefix & Rates
                    </h4>
                    <button
                      onClick={() => setShowAddCustomCountry(false)}
                      className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1 cursor-pointer"
                    >
                      <XMarkIcon className="w-4 h-4" /> Close
                    </button>
                  </div>

                  {/* Real-Time Live Availability Badge for Custom Country */}
                  <div
                    className={`p-3 rounded-xl border text-xs font-semibold flex items-center gap-2.5 transition-all ${
                      customCountryValidation.status === "already_enabled"
                        ? "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300"
                        : customCountryValidation.status === "catalog_match"
                        ? "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
                        : customCountryValidation.status === "available"
                        ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                        : "bg-white/80 dark:bg-gray-800/80 border-gray-200 dark:border-gray-700 text-gray-500"
                    }`}
                  >
                    {customCountryValidation.status === "already_enabled" ? (
                      <XCircleIcon className="w-5 h-5 text-rose-500 shrink-0 animate-pulse" />
                    ) : customCountryValidation.status === "catalog_match" ? (
                      <InformationCircleIcon className="w-5 h-5 text-blue-500 shrink-0" />
                    ) : customCountryValidation.status === "available" ? (
                      <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0" />
                    ) : (
                      <InformationCircleIcon className="w-5 h-5 text-gray-400 shrink-0" />
                    )}
                    <span>{customCountryValidation.message}</span>
                  </div>

                  <form onSubmit={handleAddCustomCountry} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Country Name *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Sri Lanka"
                          value={customCountryForm.str_country_name}
                          onChange={(e) =>
                            setCustomCountryForm({ ...customCountryForm, str_country_name: e.target.value })
                          }
                          className="w-full px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Country Calling Code (Prefix) *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. 94 or +94"
                          value={customCountryForm.str_calling_code}
                          onChange={(e) =>
                            setCustomCountryForm({ ...customCountryForm, str_calling_code: e.target.value })
                          }
                          className="w-full px-3 py-2 text-xs font-mono rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    {/* Dual Pricing: Display Price & Deduct (Sell) Price */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                          Marketing Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-gray-500 block">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_marketing_display}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_marketing_display: e.target.value,
                                  flt_marketing_deduct: e.target.value, // Keep same by default
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border rounded-lg dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 block">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_marketing_deduct}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_marketing_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border border-green-500 rounded-lg dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                          Utility Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-gray-500 block">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_utility_display}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_utility_display: e.target.value,
                                  flt_utility_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border rounded-lg dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 block">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_utility_deduct}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_utility_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border border-green-500 rounded-lg dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                          Authentication Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-gray-500 block">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_authentication_display}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_authentication_display: e.target.value,
                                  flt_authentication_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border rounded-lg dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 block">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_authentication_deduct}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_authentication_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border border-green-500 rounded-lg dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700 space-y-2">
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                          Service Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-[10px] text-gray-500 block">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_service_display}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_service_display: e.target.value,
                                  flt_service_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border rounded-lg dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-500 block">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              value={customCountryForm.flt_service_deduct}
                              onChange={(e) =>
                                setCustomCountryForm({
                                  ...customCountryForm,
                                  flt_service_deduct: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1 text-xs font-mono border border-green-500 rounded-lg dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAddCustomCountry(false)}
                        className="px-3 py-1.5 text-xs text-gray-600 rounded-lg border hover:bg-gray-50 cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={customCountryValidation.status === "already_enabled" || !customCountryForm.str_country_name || !customCountryForm.str_calling_code}
                        className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Save & Enable Country
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Table 1: Enabled Countries (with Display & Deduct inputs) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                    Enabled Countries ({accountEnabledRates.length} Enabled)
                  </h4>
                </div>

                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-2xl">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                    <thead className="bg-green-50/60 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 dark:text-gray-300 w-12">Enabled</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Country</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">Code</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 dark:text-gray-300">
                          <div>Marketing (₹)</div>
                          <div className="text-[10px] text-gray-400 font-normal flex justify-center gap-6">
                            <span>Display</span>
                            <span>Deduct</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 dark:text-gray-300">
                          <div>Utility (₹)</div>
                          <div className="text-[10px] text-gray-400 font-normal flex justify-center gap-6">
                            <span>Display</span>
                            <span>Deduct</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 dark:text-gray-300">
                          <div>Authentication (₹)</div>
                          <div className="text-[10px] text-gray-400 font-normal flex justify-center gap-6">
                            <span>Display</span>
                            <span>Deduct</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600 dark:text-gray-300">
                          <div>Service (₹)</div>
                          <div className="text-[10px] text-gray-400 font-normal flex justify-center gap-6">
                            <span>Display</span>
                            <span>Deduct</span>
                          </div>
                        </th>
                        <th className="px-3 py-3 text-center font-semibold text-gray-600 dark:text-gray-300">Status</th>
                        <th className="px-3 py-3 text-right font-semibold text-gray-600 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {modalFilteredEnabled.map((row, index) => {
                        const isIndia =
                          row.str_country_code === "IN" || (row.str_country_name || "").toLowerCase() === "india";

                        return (
                          <tr key={row.int_country_id || index} className="hover:bg-green-50/30 dark:hover:bg-gray-700/30">
                            <td className="px-3 py-2.5 text-center">
                              <input
                                type="checkbox"
                                checked={true}
                                disabled={isIndia}
                                onChange={() => handleDisableCountry(index, row)}
                                className="rounded text-green-600 focus:ring-green-500 w-4 h-4 cursor-pointer disabled:opacity-50"
                              />
                            </td>

                            <td className="px-4 py-2.5 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                              {row.str_country_name} {isIndia && <span className="text-[11px] text-green-600 font-bold">(Default)</span>}
                            </td>

                            <td className="px-3 py-2.5 font-mono text-blue-600 dark:text-blue-400 font-semibold whitespace-nowrap">
                              {row.str_calling_code || "+91"}
                            </td>

                            {/* Marketing Display & Deduct */}
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_marketing_display ?? 1.0}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_marketing_display", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
                                />
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_marketing_deduct ?? 1.0}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_marketing_deduct", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-green-500 rounded-lg dark:bg-gray-700 font-semibold"
                                />
                              </div>
                            </td>

                            {/* Utility Display & Deduct */}
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_utility_display ?? 0.15}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_utility_display", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
                                />
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_utility_deduct ?? 0.15}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_utility_deduct", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-green-500 rounded-lg dark:bg-gray-700 font-semibold"
                                />
                              </div>
                            </td>

                            {/* Authentication Display & Deduct */}
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_authentication_display ?? 0.15}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_authentication_display", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
                                />
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_authentication_deduct ?? 0.15}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_authentication_deduct", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-green-500 rounded-lg dark:bg-gray-700 font-semibold"
                                />
                              </div>
                            </td>

                            {/* Service Display & Deduct */}
                            <td className="px-4 py-2.5">
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_service_display ?? (row.flt_utility_display ?? 0.15)}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_service_display", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
                                />
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={row.flt_service_deduct ?? (row.flt_utility_deduct ?? 0.15)}
                                  onChange={(e) =>
                                    handleRateInputChange(index, "flt_service_deduct", e.target.value)
                                  }
                                  className="w-16 px-2 py-1 text-center font-mono border border-green-500 rounded-lg dark:bg-gray-700 font-semibold"
                                />
                              </div>
                            </td>

                            <td className="px-3 py-2.5 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                Enabled
                              </span>
                            </td>

                            <td className="px-3 py-2.5 text-right">
                              {!isIndia && (
                                <button
                                  type="button"
                                  onClick={() => handleDisableCountry(index, row)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Disable country"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Table 2: Other Countries (Not Enabled) */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                  <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">
                    Other Countries (Not Enabled) ({accountOtherCountries.length} Countries)
                  </h4>
                </div>

                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-2xl max-h-60">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700/60 sticky top-0">
                      <tr>
                        <th className="px-4 py-2.5 text-center font-semibold text-gray-600 dark:text-gray-300 w-24">Enable</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Country Name</th>
                        <th className="px-4 py-2.5 text-left font-semibold text-gray-600 dark:text-gray-300">Country Code</th>
                        <th className="px-4 py-2.5 text-right font-semibold text-gray-600 dark:text-gray-300">Meta Baseline Rate</th>
                        <th className="px-4 py-2.5 text-center font-semibold text-gray-600 dark:text-gray-300">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {modalFilteredOther.map((c) => (
                        <tr key={c.int_country_id || c.str_country_code} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleEnableCountry(c)}
                              className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 font-semibold text-[11px] transition-all cursor-pointer"
                            >
                              + Enable
                            </button>
                          </td>
                          <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{c.str_country_name}</td>
                          <td className="px-4 py-2 font-mono text-gray-600 dark:text-gray-400">{c.str_calling_code || c.str_country_code}</td>
                          <td className="px-4 py-2 text-right font-mono text-gray-500">
                            Marketing: ₹{Number(c.flt_marketing_rate || 0).toFixed(4)}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className="text-[11px] text-gray-400 italic">Not Enabled</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
            )}

            {/* Modal Sticky Footer */}
            <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsPermModalOpen(false)}
                className="px-5 py-2.5 text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingPerms}
                onClick={handleSaveAccountCountryRates}
                className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl transition-all shadow-md shadow-green-600/20 text-xs sm:text-sm cursor-pointer disabled:opacity-50"
              >
                {isSavingPerms ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Save Country Rates
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Account User Settings Modal Dialog ── */}
      {isUserSettingsModalOpen && selectedUserSettingsAcc && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-5">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-2xl w-full flex flex-col shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50/90 to-white dark:from-gray-900/60 dark:to-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-purple-500/20">
                  <Cog6ToothIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    Account User Settings: <span className="text-purple-600 dark:text-purple-400 font-mono">{selectedUserSettingsAcc.str_company_name || selectedUserSettingsAcc.str_account_code || `ACC${String(selectedUserSettingsAcc.int_account_id).padStart(6, "0")}`}</span>
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Configure delivery deduction, credit visibility, failed masking cap, and automatic failure refund delay
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsUserSettingsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {loadingUserSettings ? (
                <div className="py-12 text-center text-gray-400">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent mb-2" />
                  <div>Loading account user settings...</div>
                </div>
              ) : (
                <>
                  {/* Setting 1: Deduct On Failed */}
                  <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-750/30 hover:border-emerald-300 dark:hover:border-emerald-700 transition-all">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-900 dark:text-white">
                            1. Deduct On Failed
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            userSettingsForm.bool_deduct_on_failed
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {userSettingsForm.bool_deduct_on_failed ? "Enabled (Default)" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When enabled, credits will be deducted for failed message & campaign delivery attempts. If disabled, failed delivery attempts are not charged (credit will not be cut).
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={userSettingsForm.bool_deduct_on_failed}
                          onChange={(e) =>
                            setUserSettingsForm((prev) => ({
                              ...prev,
                              bool_deduct_on_failed: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-200 peer-focus:outline-hidden rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Setting 2: Show Credits */}
                  <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-750/30 hover:border-blue-300 dark:hover:border-blue-700 transition-all">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-900 dark:text-white">
                            2. Show Credits
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            userSettingsForm.bool_show_credits
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {userSettingsForm.bool_show_credits ? "Enabled (Default)" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When enabled, this user can see cut credit, total debited amounts, and message prices in reports and campaign summaries. When disabled, cut prices/credits are hidden from the user.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={userSettingsForm.bool_show_credits}
                          onChange={(e) =>
                            setUserSettingsForm((prev) => ({
                              ...prev,
                              bool_show_credits: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-200 peer-focus:outline-hidden rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Setting 3: Failed Cap & Failed Cap % */}
                  <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-750/30 hover:border-purple-300 dark:hover:border-purple-700 transition-all space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-900 dark:text-white">
                            3. Failed Cap & Cap %
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            userSettingsForm.bool_failed_cap_enabled
                              ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {userSettingsForm.bool_failed_cap_enabled ? `Active (${userSettingsForm.num_failed_cap_percent}%)` : "Disabled (Default)"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When enabled, a percentage of failed messages will be masked and reported as Sent/Delivered. When disabled, actual raw failure counts are shown in reports.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={userSettingsForm.bool_failed_cap_enabled}
                          onChange={(e) =>
                            setUserSettingsForm((prev) => ({
                              ...prev,
                              bool_failed_cap_enabled: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-200 peer-focus:outline-hidden rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    {userSettingsForm.bool_failed_cap_enabled && (
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Failed Cap Percentage (%):
                          </label>
                          <div className="relative w-32">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={userSettingsForm.num_failed_cap_percent}
                              onChange={(e) =>
                                setUserSettingsForm((prev) => ({
                                  ...prev,
                                  num_failed_cap_percent: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                                }))
                              }
                              className="w-full pr-8 pl-3 py-1.5 text-xs font-bold rounded-xl border border-purple-300 dark:border-purple-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 text-right"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                              %
                            </span>
                          </div>
                        </div>

                        {/* Dynamic Formula Explanation Box */}
                        <div className="p-3 rounded-xl bg-purple-50/80 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/40 text-[11px] text-purple-900 dark:text-purple-200 leading-relaxed">
                          <span className="font-bold">💡 Live Calculation Preview:</span> If this user sends a campaign with <span className="font-bold underline">100 failed messages</span>:
                          <ul className="list-disc list-inside mt-1 space-y-0.5">
                            <li><span className="font-bold text-emerald-600 dark:text-emerald-400">{userSettingsForm.num_failed_cap_percent}%</span> ({userSettingsForm.num_failed_cap_percent} messages) will be masked & reported as <span className="font-semibold text-emerald-600 dark:text-emerald-400">Sent</span></li>
                            <li>Only <span className="font-bold text-rose-600 dark:text-rose-400">{100 - userSettingsForm.num_failed_cap_percent}%</span> ({100 - userSettingsForm.num_failed_cap_percent} messages) will be shown as <span className="font-semibold text-rose-600 dark:text-rose-400">Failed</span></li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Setting 4: Refund Failed Credit & Delay (hrs) */}
                  <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-750/30 hover:border-amber-300 dark:hover:border-amber-700 transition-all space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-gray-900 dark:text-white">
                            4. Refund Failed Credit & Delay
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            userSettingsForm.bool_refund_failed_credit
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {userSettingsForm.bool_refund_failed_credit ? `Active (${userSettingsForm.num_refund_delay_hours} hrs)` : "Disabled (Default)"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When enabled, credit for failed messages will be automatically refunded back to the user's account wallet after the specified delay in hours.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={userSettingsForm.bool_refund_failed_credit}
                          onChange={(e) =>
                            setUserSettingsForm((prev) => ({
                              ...prev,
                              bool_refund_failed_credit: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-200 peer-focus:outline-hidden rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-amber-500"></div>
                      </label>
                    </div>

                    {userSettingsForm.bool_refund_failed_credit && (
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Refund Delay (Hours):
                          </label>
                          <div className="relative w-36">
                            <input
                              type="number"
                              min="0"
                              max="720"
                              step="1"
                              value={userSettingsForm.num_refund_delay_hours}
                              onChange={(e) =>
                                setUserSettingsForm((prev) => ({
                                  ...prev,
                                  num_refund_delay_hours: Math.max(0, Number(e.target.value) || 0),
                                }))
                              }
                              className="w-full pr-12 pl-3 py-1.5 text-xs font-bold rounded-xl border border-amber-300 dark:border-amber-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 text-right"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">
                              hrs
                            </span>
                          </div>
                        </div>

                        {/* Dynamic Formula Explanation Box */}
                        <div className="p-3 rounded-xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 text-[11px] text-amber-900 dark:text-amber-200 leading-relaxed">
                          <span className="font-bold">💡 Automatic Refund Rules:</span>
                          <ul className="list-disc list-inside mt-1 space-y-0.5">
                            <li>Credits are refunded automatically after <span className="font-bold underline">{userSettingsForm.num_refund_delay_hours || 0} hour(s)</span>.</li>
                            <li>
                              Honors Failed Cap: If 100 messages fail with a {userSettingsForm.bool_failed_cap_enabled ? (userSettingsForm.num_failed_cap_percent || 0) : 0}% cap, only the visible <span className="font-bold underline">{100 - (userSettingsForm.bool_failed_cap_enabled ? (userSettingsForm.num_failed_cap_percent || 0) : 0)} failed messages</span> will receive credit refund.
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/60 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsUserSettingsModalOpen(false)}
                className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveUserSettings}
                disabled={savingUserSettings || loadingUserSettings}
                className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-purple-600/20 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingUserSettings ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                    Saving Settings...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4 mr-1.5" />
                    Save User Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Destination Country Right Sidebar (Slide-Over Drawer) ── */}
      {isCountryModalOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop with subtle blur */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300 animate-in fade-in"
            onClick={() => setIsCountryModalOpen(false)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10 pointer-events-none">
            <div className="w-screen max-w-md bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col pointer-events-auto transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
              
              {/* Drawer Header */}
              <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50/80 to-white dark:from-gray-900/80 dark:to-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg shadow-xs shrink-0">
                    <GlobeAmericasIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {selectedCountry ? `Edit: ${selectedCountry.str_country_name}` : "Add Destination Country"}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      WhatsApp Meta baseline pricing & catalog entry
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCountryModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-all"
                  title="Close sidebar"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body (Scrollable Form) */}
              <form onSubmit={handleSaveCountry} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
                
                {/* Real-Time Live Availability Badge */}
                <div
                  className={`p-3.5 rounded-2xl border transition-all duration-200 ${
                    countryValidation.status === "duplicate"
                      ? "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/60 text-rose-800 dark:text-rose-300"
                      : countryValidation.status === "available"
                      ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-300"
                      : "bg-gray-50 dark:bg-gray-750/50 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    {countryValidation.status === "duplicate" ? (
                      <XCircleIcon className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 animate-pulse" />
                    ) : countryValidation.status === "available" ? (
                      <CheckCircleIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <InformationCircleIcon className="w-5 h-5 text-blue-500 shrink-0" />
                    )}
                    <div className="text-xs font-semibold leading-relaxed">
                      {countryValidation.message}
                    </div>
                  </div>
                </div>

                {/* Country Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Country Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. United Kingdom"
                    value={countryForm.str_country_name}
                    onChange={(e) => setCountryForm({ ...countryForm, str_country_name: e.target.value })}
                    className={`w-full px-3.5 py-2.5 text-xs rounded-xl border bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all ${
                      countryValidation.status === "duplicate" &&
                      countryValidation.duplicateCountry?.str_country_name?.toLowerCase() === (countryForm.str_country_name || "").trim().toLowerCase()
                        ? "border-rose-500 focus:ring-rose-500"
                        : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                    }`}
                  />
                </div>

                {/* ISO Code & Calling Code */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      ISO Code <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={3}
                      placeholder="e.g. GB"
                      value={countryForm.str_country_code}
                      onChange={(e) =>
                        setCountryForm({ ...countryForm, str_country_code: e.target.value.toUpperCase().trim() })
                      }
                      className={`w-full px-3.5 py-2.5 text-xs uppercase font-mono rounded-xl border bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all ${
                        countryValidation.status === "duplicate" &&
                        countryValidation.duplicateCountry?.str_country_code?.toUpperCase() === (countryForm.str_country_code || "").trim().toUpperCase()
                          ? "border-rose-500 focus:ring-rose-500"
                          : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Calling Code <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. +44"
                      value={countryForm.str_calling_code}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (val && !val.startsWith("+")) val = `+${val}`;
                        setCountryForm({ ...countryForm, str_calling_code: val });
                      }}
                      className={`w-full px-3.5 py-2.5 text-xs font-mono rounded-xl border bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all ${
                        countryValidation.status === "duplicate" &&
                        countryValidation.duplicateCountry?.str_calling_code === (countryForm.str_calling_code || "").trim()
                          ? "border-rose-500 focus:ring-rose-500"
                          : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                      }`}
                    />
                  </div>
                </div>

                {/* Baseline Pricing Header */}
                <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                    WhatsApp Business Meta Baseline Rates (INR ₹)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-200 dark:border-gray-700 space-y-1">
                      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 block">Marketing (₹)</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={countryForm.flt_marketing_rate}
                        onChange={(e) => setCountryForm({ ...countryForm, flt_marketing_rate: Number(e.target.value) })}
                        className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-200 dark:border-gray-700 space-y-1">
                      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 block">Utility (₹)</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={countryForm.flt_utility_rate}
                        onChange={(e) => setCountryForm({ ...countryForm, flt_utility_rate: Number(e.target.value) })}
                        className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-200 dark:border-gray-700 space-y-1">
                      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 block">Authentication (₹)</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={countryForm.flt_authentication_rate}
                        onChange={(e) => setCountryForm({ ...countryForm, flt_authentication_rate: Number(e.target.value) })}
                        className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-200 dark:border-gray-700 space-y-1">
                      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 block">Auth-Intl (₹)</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={countryForm.flt_authentication_international_rate}
                        onChange={(e) => setCountryForm({ ...countryForm, flt_authentication_international_rate: Number(e.target.value) })}
                        className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50/80 dark:bg-gray-750/50 border border-gray-200 dark:border-gray-700 space-y-1">
                      <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 block">Service (₹)</span>
                      <input
                        type="number"
                        step="0.0001"
                        value={countryForm.flt_service_rate}
                        onChange={(e) => setCountryForm({ ...countryForm, flt_service_rate: Number(e.target.value) })}
                        className="w-full px-2.5 py-1.5 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Active Checkbox */}
                <div className="pt-2">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      id="bool_is_active_country_drawer"
                      checked={countryForm.bool_is_active}
                      onChange={(e) => setCountryForm({ ...countryForm, bool_is_active: e.target.checked })}
                      className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                    />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Destination Country is Active
                    </span>
                  </label>
                </div>
              </form>

              {/* Drawer Footer */}
              <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/60 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setIsCountryModalOpen(false)}
                  className="px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCountry}
                  disabled={countryValidation.status === "duplicate" || !countryForm.str_country_name || !countryForm.str_country_code || !countryForm.str_calling_code}
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-blue-600/20 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="w-4 h-4 mr-1.5" />
                  Save Destination Country
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
